<template>
  <section
    :id="sectionId"
    ref="carousel"
    class="scrolling-box-section"
  >
    <div class="section-bg">
      <div class="bg-img"></div>
    </div>
    <div class="container row-reverse">
      <div class="scrolling-box-animation">
        <div
          :class="boxBackgroundClass"
          :style="`border-radius: ${boxRadius}px`"
        ></div>
        <div class="boxes">
          <div
            v-for="(item, itemsIndex) in data"
            :key="itemsIndex"
            class="box"
          >
            <NuxtImg
              :src="item.illustration"
              alt="illustration"
              class="box-img"
            />
            <h2
              class="box-title"
              v-html="item.title"
            ></h2>
            <p
              class="box-description"
              v-html="item.description"
            ></p>
          </div>
        </div>
      </div>
      <div class="apply-section">
        <h2
          class="apply-section-title"
          v-html="sectionTitle"
        ></h2>
        <h2
          class="apply-section-subtitle"
          v-html="sectionSubtitle"
        ></h2>
        <p
          class="apply-section-description"
          v-html="sectionDescription"
        ></p>

        <div
          v-if="sectionItems.length"
          class="apply-section-items"
        >
          <div
            v-for="(item, key) in sectionItems"
            :key="key"
            class="item"
          >
            <p
              class="item-title"
              v-html="item.title"
            />
            <div class="item-tooltip">
              <p
                class="item-description"
                v-html="item.description"
              />
              <Tooltip>
                <span>{{ item.tooltip }}</span>
              </Tooltip>
            </div>
          </div>
        </div>

        <div class="buttons">
          <ApplyButton
            v-if="isApplyButton"
            :cta-section="sectionId"
            :text="buttonText"
            :page-name="pageName"
            class="proxify-btn"
          />
          <nuxt-link
            v-else
            :to="{ name: buttonRedirect }"
            :aria-label="buttonText"
            class="proxify-btn"
          >
            {{ buttonText }}
          </nuxt-link>
        </div>
      </div>
      <div
        class="carousel-mobile"
        :class="{ smaller }"
      >
        <div
          ref="box"
          class="carousel-box-wrapper"
        >
          <div class="carousel-box"></div>
        </div>
        <div
          id="items"
          ref="items"
          class="items"
          :style="`left:${left}px`"
        >
          <div
            v-for="(item, itemsIndex) in data"
            :key="`${sectionId}-${itemsIndex}`"
            class="carousel-item"
            :class="{ active: itemsIndex === index }"
          >
            <div class="item-wrapper">
              <img
                :src="item.illustration"
                alt="illustration"
                class="box-img"
              />
            </div>
            <h2
              class="items-title"
              v-html="item?.titleMobile ?? item.title"
            ></h2>
            <p
              class="items-description"
              v-html="item.description"
            ></p>
          </div>
        </div>
      </div>
      <div class="indicators">
        <span
          v-for="(item, idx) in data"
          :key="idx"
          :class="idx === index && 'active-color'"
        ></span>
      </div>
      <div class="buttons-mobile">
        <ApplyButton
          v-if="isApplyButton"
          :cta-section="sectionId"
          :text="buttonText"
          :page-name="pageName"
          class="proxify-btn"
        />
        <nuxt-link
          v-else
          :to="{
            name: buttonRedirect,
            meta: {
              ctaName: buttonText,
              pageName: pageName,
              ctaSection: sectionId,
            },
          }"
          :aria-label="buttonText"
          class="proxify-btn"
        >
          {{ buttonText }}
        </nuxt-link>
      </div>
    </div>
  </section>
</template>

<script>
import carousel from '~/mixins/carousel';
import ApplyButton from '~/components/Common/ApplyButton/ApplyButton.vue';
import Tooltip from '~/components/Common/UI/Tooltip/Tooltip.vue';

export default {
  components: { Tooltip, ApplyButton },
  mixins: [carousel],
  props: {
    sectionId: {
      type: String,
      required: true,
    },
    sectionTitle: {
      type: String,
      required: true,
    },
    sectionSubtitle: {
      type: String,
      required: true,
    },
    sectionDescription: {
      type: String,
      required: true,
    },
    sectionItems: {
      type: Array,
      default: () => [],
    },
    data: {
      type: Array,
      required: true,
    },
    isApplyButton: {
      type: Boolean,
      default: true,
    },
    buttonText: {
      type: String,
      required: true,
    },
    buttonRedirect: {
      type: String,
      default: '',
    },
    smaller: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showAnimations: false,
      startWithCircle: true,
      boxForm: 'circle',
      boxRadius: 40,
      boxBackgroundClass: 'box-background box-background-hide',
      index: 0,
      left: 0,
    };
  },
  computed: {
    pageName() {
      return this.$route.name === 'index' ? 'homepage' : this.$route.name;
    },
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleScroll() {
      const scrollingBoxScrollTop = -document
        .querySelector(`#${this.sectionId}`)
        .getBoundingClientRect().top;

      if (
        scrollingBoxScrollTop < 0 ||
        scrollingBoxScrollTop >=
          document.querySelector(`#${this.sectionId}`).clientHeight -
            window.innerHeight
      ) {
        this.boxBackgroundClass = 'box-background box-background-hide';
      } else {
        this.boxBackgroundClass = 'box-background';
      }

      const boxHeight = 410;

      const boxForms = this.startWithCircle
        ? ['circle', 'square']
        : ['square', 'circle'];

      const currentBoxIndex =
        Math.floor(scrollingBoxScrollTop / boxHeight) % boxForms.length;
      this.boxForm = boxForms[currentBoxIndex];

      let boxRadius = Math.round((scrollingBoxScrollTop % 410) / 2.05);

      if (
        (this.startWithCircle && this.boxForm === 'circle') ||
        (this.boxForm === 'circle' && scrollingBoxScrollTop >= boxHeight)
      ) {
        boxRadius = 200 - boxRadius;
      }

      this.boxRadius = Math.max(boxRadius, 24);
    },
    setActiveItem(type) {
      const isFirstItem = this.index === 0;
      const isLastItem = this.index === this.data.length - 1;
      const isNext = type === 'next';
      const isPrevious = type === 'prev';

      if (isNext && !isLastItem) {
        this.left -= this.$refs.items.firstChild.offsetWidth;
        this.index++;
      } else if (isPrevious && !isFirstItem) {
        this.left += this.$refs.items.firstChild.offsetWidth;
        this.index--;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './ScrollingBox.scss';
</style>
